// Comprehensive Connecticut family law data based on FindLaw and official Connecticut sources
const connecticutData = {
  'connecticut': {
    name: 'Connecticut',
    overview: {
      keyPoints: [
        'Connecticut courts determine custody based on the "best interests of the child" standard (C.G.S. § 46b-56)',
        'The state presumes that joint legal custody is in the best interest of the child when parents agree, but courts make individual determinations in contested cases',
        'Upon filing for divorce or custody, "Automatic Orders" go into effect immediately, freezing assets and preventing the removal of children from the state',
        'Connecticut requires all parents in custody cases to complete a mandatory Parenting Education Program (PEP)',
        'The state distinguishes between "Legal Custody" (decision-making) and "Physical Custody" (residence)',
        'Family Relations Counselors often mediate disputes and conduct custody evaluations if parents cannot agree'
      ],
      uniqueFeatures: [
        'The "Automatic Orders" (P.B. § 25-5) are strictly enforced and apply to both plaintiff (upon signing) and defendant (upon service)',
        'Connecticut has a "90-day waiting period" (cooling-off period) after the return date before a divorce can be finalized',
        'Family Support Magistrates specifically handle many child support and paternity matters separate from Superior Court judges',
        'The state uses a specific "Shared Physical Custody" deviation in child support calculations when the non-custodial parent has significantly more time than standard visitation'
      ]
    },
    legalRequirements: {
      residency: 'One party must be a resident of Connecticut for at least 12 months before a final divorce decree can be granted, though you can file sooner.',
      filing: 'File a Summons, Complaint, and Notice of Automatic Orders in the Superior Court for the Judicial District where either party resides',
      paternity: 'Established via Acknowledgment of Paternity at birth, genetic testing, or court order through the Family Support Magistrate',
      documentation: 'Summons (JD-FM-3), Complaint (JD-FM-159), Notice of Automatic Orders (JD-FM-158), and Financial Affidavit',
      uniqueRules: [
        'Mandatory Parenting Education Program must be completed within 60 days of the return date',
        'Full financial disclosure is mandatory; failure to disclose assets can lead to the judgment being reopened later',
        'Case management dates are set early to schedule mediation and track progress'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Refers to where the child resides; can be "Primary" (one parent) or "Shared" (substantial time with both)',
      legalCustody: 'Refers to decision-making authority for major life issues (health, education, religion); "Joint Legal" is the most common award',
      jointCustody: 'Usually refers to Joint Legal Custody; does not necessarily mean 50/50 physical time',
      visitation: 'Often called "parenting time"; schedules vary from "reasonable and flexible" to highly detailed specific schedules',
      specialProvisions: [
        'Attorneys for the Minor Child (AMC) or Guardians ad Litem (GAL) may be appointed in high-conflict cases to represent the child',
        'Connecticut courts can order "bird-nesting" (children stay in home, parents rotate) though it is generally a temporary measure',
        'Third-party visitation claims (grandparents) faces a high burden of proof (must prove "real and significant harm" to child if denied)'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Specific schedule for weekly parenting time',
        'Holiday and vacation rotation (typically odd/even years)',
        'Decision-making authority allocation (Joint or Sole)',
        'Child support amounts and division of unreimbursed medical/extracurricular expenses',
        'Health insurance coverage responsibilities',
        'Relocation provisions (especially for moves out of state)',
        'Dispute resolution mechanisms'
      ],
      tips: [
        'Review the "Automatic Orders" carefully to avoid contempt of court during the process',
        'Be specific about transportation duties, especially if parents live in different towns (Connecticut traffic can be a factor)',
        'Include a "Right of First Refusal" if you want the option to care for the child when the other parent is working',
        'Specify how college expenses will be handled (Connecticut courts can order "Educational Support Orders" for college)'
      ]
    },
    childSupport: {
      guidelines: 'Connecticut uses the "Income Shares Model" based on net weekly income of both parents',
      factors: [
        'Combined net weekly income of both parents',
        'Number of children',
        'Cost of health insurance premiums for the child',
        'Work-related childcare expenses',
        'Unreimbursed medical expenses',
        'Prior support obligations',
        'Significant visitation (deviation may apply)'
      ],
      calculation: 'Calculated using the Connecticut Child Support Guidelines Worksheet; creates a presumptive amount that courts rarely deviate from without cause',
      modifications: 'Can be modified if there is a "substantial change in circumstances" (typically a 15% deviation from the current guidelines)',
      enforcement: 'Enforced by Support Enforcement Services (SES) via wage withholding, tax intercept, and license suspension'
    },
    resources: {
      courts: [
        {
          name: 'Connecticut Judicial Branch - Family Matters',
          url: 'https://www.jud.ct.gov/lawlib/law/family.htm',
          description: 'Official portal for forms, case lookups, and self-help guides'
        },
        {
          name: 'Understanding Child Support in CT',
          url: 'https://www.jud.ct.gov/childsupport/',
          description: 'Official guidelines and publications explaining support calculations'
        },
        {
          name: 'Statewide Legal Services of Connecticut',
          url: 'https://slsct.org/',
          description: 'Legal aid intake for low-income residents'
        }
      ],
      support: [
        {
          name: 'Connecticut Dept of Social Services - Child Support',
          url: 'https://portal.ct.gov/DSS/Child-Support/Child-Support-Services',
          description: 'Apply for support enforcement and modification services'
        },
        {
          name: 'CtLawHelp.org',
          url: 'https://ctlawhelp.org/en/home',
          description: 'Plain English guides to divorce and custody in Connecticut'
        },
        {
          name: 'Connecticut Coalition Against Domestic Violence',
          url: 'http://www.ctcadv.org/',
          description: 'Resources and shelter information for victims'
        }
      ],
      legal: [
        {
          name: 'Connecticut Bar Association - Find a Lawyer',
          url: 'https://www.ctbar.org/public/find-a-lawyer',
          description: 'Directory of licensed attorneys in Connecticut'
        },
        {
          name: 'Family Services (Judicial Branch)',
          url: 'https://www.jud.ct.gov/family/',
          description: 'Information on mediation and custody evaluations provided by the court'
        }
      ],
      educational: [
        {
          name: 'Parenting Education Program (PEP) List',
          url: 'https://www.jud.ct.gov/family/parenting.htm',
          description: 'List of approved providers for the mandatory parenting class'
        },
        {
          name: 'A Guide to Representing Yourself in Family Court',
          url: 'https://www.jud.ct.gov/Publications/FM218.pdf',
          description: 'Official handbook for pro se litigants'
        }
      ]
    },
    bestInterestsFactors: [
      'The temperament and developmental needs of the child',
      'The capacity and the disposition of the parents to understand and meet the needs of the child',
      'Any relevant and material information obtained from the child, including the informed preferences of the child',
      'The wishes of the child\'s parents as to custody',
      'The past and current interaction and relationship of the child with each parent, the child\'s siblings and any other person who may significantly affect the best interests of the child',
      'The willingness and ability of each parent to facilitate and encourage such continuing parent-child relationship between the child and the other parent',
      'Any manipulation by or coercive behavior of the parents in an effort to involve the child in the parents\' dispute',
      'The ability of each parent to be actively involved in the life of the child',
      'The child\'s adjustment to his or her home, school and community environments',
      'The length of time that the child has lived in a stable and satisfactory environment',
      'The stability of the child\'s existing or proposed residence',
      'The mental and physical health of all individuals involved',
      'The effect on the child of the actions of an abuser, if any domestic violence has occurred'
    ]
  }
};

export default connecticutData;